// ---------------------------------------------------------------------------
// Publications Data
// ---------------------------------------------------------------------------
//
// To add a new publication:
//   1. Drop images into src/assets/publications/
//   2. Import them below
//   3. Add an entry to the array
//
// Fields:
//   id            – unique number (highest = most recent, shown first)
//   status        – 'published' | 'upcoming' | 'editorials' (controls filter tabs)
//   title         – HTML allowed (use <br /> for line breaks)
//   description   – short summary
//   authors       – author string (optional)
//   position      – 'left' | 'right' (layout side)
//   image         – imported image for the card (null if unavailable)
//   imageContain  – true = object-fit:contain, false = cover (default false)
//   imageBgWhite  – true = white card background (default false)
//   preview       – if present, adds a preview button that opens a modal:
//       .image         – imported image for the modal
//       .description   – full abstract / description text (HTML allowed)
//       .links         – { publisherUrl, githubUrl, pdfUrl } (empty string = not yet available)
//       .images        – array of imported images for carousel (optional, overrides .image)
//       .reviewNotice  – shown when no links are available
//       .projectPageUrl – optional URL to a project detail page; renders a button in the modal

import loamImage from '../assets/publications/LOAM1_firstfigure.png';
import loamAssemblyImage from '../assets/publications/LOAM1_assembly.png';
import mantisImage from '../assets/publications/Mantis1.png';
import mantisPosterImage from '../assets/publications/MANTIS_poster.png';

export const publicationsData = [
  {
    id: 1,
    status: 'published',
    title: 'LOAM: Low-Cost Low-Power Activity-Aware<br />Soil Moisture Sensing Platform',
    description:
      'A battery-free LoRa sensor that offloads connectivity cost to a mobile gateway using deterministic, behavior-based duty cycling. Published as arXiv:2604.26303; submitted to ACM IMWUT 2026.',
    authors: 'Thoene, J., Kamil, O., Alkadee, T., Jung, N., & Arora, N.',
    position: 'left',
    image: loamImage,
    imageContain: true,
    imageBgWhite: true,
    preview: {
      projectPageUrl: '/project?id=32',
      image: loamAssemblyImage,
      images: [loamAssemblyImage, loamImage],
      description: `Deep understanding of a field's soil moisture content is the lead-
ing indicator for predicting crop yields and making data driven
decisions for irrigation and application of topical chemicals for
drought resilience. Despite this importance, the cost of adopting
and maintaining IoT infrastructure prevents modern farms from em-
ploying widespread real time soil moisture sensors. LOAM presents
an end-to-end platform of buried battery-free sensor nodes and
a mobile basestation that leverages the farmer's daily routine for
data retrieval. Each LOAM node features a self-powered galvanic
soil-moisture probe, employing a high impedance analog front
end to enable durability. Operating entirely on harvested solar en-
ergy for up to 21 days on a single capacitor charge, each node
collects soil moisture, temperature, and environment condition
data. Using a predictable finite-state machine, handshake-based
data exchanges occur with a basestation affixed to standard farm-
ing vehicles designed to listen for the nodes while moving through
the farm. LOAM organizes all sensor, link-quality, and location data
into an easy-to-interpret dashboard to seamlessly integrate with
the farmer's everyday routine. Costing less than $35, LOAM is a
financially accessible, accurate, and easily scalable platform that
enables persistent, regular data collection from the most rural plots
without adding to or impeding farming operations. Experimental
evaluation demonstrates reliable communication over 1 km at 2
dBm transmit power, stable sensor readings over 70 days of indoor
operation, and continuous data recovery during multiple periods
of intermittent connection.`,
      links: {
        publisherUrl: 'https://arxiv.org/abs/2604.26303',
        githubUrl: '',
        pdfUrl: '',
      },
      reviewNotice:
        'Preprint available on arXiv; the peer-reviewed venue links will be added after publication.',
    },
  },
  {
    id: 2,
    status: 'editorials',
    title:
      'HF is King: How the HAM Radio Community<br />and the Strategic Corporal Can Fight and Win in the Littorals',
    description: 'Marine Corps Association and Foundation Gazette, 2025.',
    authors: 'Thoene, J., Beckman, Z., & Sahakian, A.',
    position: 'right',
    image: null,
  },
  {
    id: 3,
    status: 'published',
    title:
      'POSTER: MANTIS: Manufacturable Application-Specific Narrowband Tunable Filters for Inference-In-Sensor',
    description:
      'Awarded Best Poster, ACM SenSys 2026 / IEEE IoT Week, Saint Malo, France, May 2026. Modular, adaptable filters for hyperspectral-informed, real-time ore grade estimation for rare earth elements.',
    authors: '',
    position: 'left',
    image: mantisPosterImage,
    imageContain: true,
    imageBgWhite: true,
    preview: {
      projectPageUrl: '/project?id=34',
      image: mantisImage,
      description: `We present MANTIS: Manufacturable Application-Specific Narrowband Tunable Filters for Inference-In-Sensor, a
framework to design and implement application-specific filters
to augment image sensing platforms with hyperspectral image-
like performance without the complexity, cost, and computational
overhead. MANTIS provides a methodology for creating custom
hyperspectral datasets collected using low-cost pushbroom HSI
systems to identify task-relevant spectral bands via machine
learning. These bands are then translated into realizable nar-
rowband optical filters, enabling inference-in-sensor without full
hyperspectral acquisition. We demonstrate the feasibility of this
approach by learning and simulating combined binary masks
based on open source datasets.`,
      links: {
        publisherUrl: '',
        githubUrl: '',
        pdfUrl: '',
      },
      reviewNotice:
        'This work is under review by the publishing editors, links will become available after publication.',
    },
  },
  {
    id: 4,
    status: 'upcoming',
    title:
      'A Novel, Self-Powered Rural Sensor Network<br />Employing Bluetooth Low Energy and Autonomous Drones',
    description:
      'IMWUT (UbiComp/ISWC 2027). Builds upon LOAM publication. May 1st submission target.',
    authors: '',
    position: 'right',
    image: null,
  },
  {
    id: 5,
    status: 'upcoming',
    title:
      'Modular, Adaptable Filters for<br />Hyperspectral-Informed Real-Time Ore Grade Estimation',
    description:
      'Nature Electronics. Modular, adaptable filters for hyperspectral-informed, real-time ore grade estimation for rare earth elements. August 1st submission target.',
    authors: '',
    position: 'left',
    image: null,
  },
];
